import { message } from 'antd'
import { useRolodexEdit } from 'features/fde'
import {
  FfsAssessmentResult,
  FfsAutocadDrawingData,
  FfsCoverSectionData,
  FfsFormDataState,
  FfsMainInfo,
} from 'pages/reporting/reports/ffs/ffsReportData'
import { RevisionMetadata } from 'pages/reporting/shared/types'
import { useCallback, useEffect, useState } from 'react'
import {
  GeckoReportingMetadata,
  LockState,
  SignatureData,
  SignoffState,
} from 'types'

/**
 * Hook for managing FFS report form data state and persistence
 */
export const useFfsFormData = (
  metadataEntryId: string,
  metadataEntries: readonly GeckoReportingMetadata[],
  isLoading: boolean
) => {
  const [formData, setFormData] = useState<FfsFormDataState>({
    mainInfo: {
      customerName: '',
      customerLocation: '',
      assetName: '',
      assetId: '',
      reportDate: new Date().toISOString(),
    },
    coverSectionData: {
      reportTitle: 'Fitness-For-Service Assessment Report',
      preparedBy: '',
      preparedFor: '',
      reportNumber: '',
      revisionNumber: '1',
    },
    autocadDrawingData: {
      hasDrawing: false,
    },
    assessmentResults: [],
    indicationIds: [],
    signoff: {
      lastRevision: 0,
    },
    revisionMetadata: {
      revisionNumber: 1,
      createdAt: new Date().toISOString(),
      isPublished: false,
    },
    lockState: {
      isLocked: false,
      startedSignoff: false,
    },
    publishedPdfPath: '',
  })

  const { rolodexPatch } = useRolodexEdit()

  // Load form data from metadata entry
  useEffect(() => {
    if (isLoading || !metadataEntryId) return

    const currentEntry = metadataEntries.find(
      (entry) => entry.id === metadataEntryId
    )

    if (!currentEntry) {
      console.warn('Current metadata entry not found')
      return
    }

    const existingData = currentEntry.data as Record<string, unknown>
    const sectionInfo = existingData.section_info as Partial<FfsFormDataState>

    const newFormData: FfsFormDataState = {
      mainInfo: (sectionInfo?.mainInfo as FfsMainInfo) || formData.mainInfo,
      coverSectionData:
        (sectionInfo?.coverSectionData as FfsCoverSectionData) ||
        formData.coverSectionData,
      autocadDrawingData:
        (sectionInfo?.autocadDrawingData as FfsAutocadDrawingData) ||
        formData.autocadDrawingData,
      assessmentResults:
        (sectionInfo?.assessmentResults as FfsAssessmentResult[]) || [],
      indicationIds: (sectionInfo?.indicationIds as string[]) || [],
      signoff: (existingData.signoff as SignoffState) || {
        lastRevision: 0,
      },
      revisionMetadata: (existingData.revisionMetadata as RevisionMetadata) || {
        revisionNumber: 1,
        createdAt: new Date().toISOString(),
        isPublished: false,
      },
      lockState: (existingData.lockState as LockState) || {
        isLocked: false,
        startedSignoff: false,
      },
      publishedPdfPath: (existingData.publishedPdfPath as string) || '',
    }

    setFormData(newFormData)
  }, [isLoading, metadataEntries, metadataEntryId, formData])

  const updateFormData = useCallback((newData: Partial<FfsFormDataState>) => {
    setFormData((prev) => ({ ...prev, ...newData }))
  }, [])

  const handleMainInfoFinish = useCallback(
    async (values: FfsMainInfo) => {
      try {
        updateFormData({ mainInfo: values })

        await rolodexPatch([
          {
            id: metadataEntryId,
            data: {
              section_info: { mainInfo: values },
            },
          },
        ])

        message.success('Main info saved successfully!')
      } catch {
        message.error('Failed to save main info')
      }
    },
    [updateFormData, metadataEntryId, rolodexPatch]
  )

  const handleCoverSectionFinish = useCallback(
    async (values: FfsCoverSectionData) => {
      try {
        updateFormData({ coverSectionData: values })

        await rolodexPatch([
          {
            id: metadataEntryId,
            data: {
              section_info: { coverSectionData: values },
            },
          },
        ])

        message.success('Cover section saved successfully!')
      } catch {
        message.error('Failed to save cover section')
      }
    },
    [updateFormData, metadataEntryId, rolodexPatch]
  )

  const handleAutocadDrawingFinish = useCallback(
    async (values: FfsAutocadDrawingData) => {
      try {
        updateFormData({ autocadDrawingData: values })

        await rolodexPatch([
          {
            id: metadataEntryId,
            data: {
              section_info: { autocadDrawingData: values },
            },
          },
        ])

        message.success('AutoCAD drawing saved successfully!')
      } catch {
        message.error('Failed to save AutoCAD drawing')
      }
    },
    [updateFormData, metadataEntryId, rolodexPatch]
  )

  const handleAssessmentResultsFinish = useCallback(
    async (values: FfsAssessmentResult[]) => {
      try {
        updateFormData({ assessmentResults: values })

        await rolodexPatch([
          {
            id: metadataEntryId,
            data: {
              section_info: { assessmentResults: values },
            },
          },
        ])

        message.success('Assessment results saved successfully!')
      } catch {
        message.error('Failed to save assessment results')
      }
    },
    [updateFormData, metadataEntryId, rolodexPatch]
  )

  const handleIndicationIdsFinish = useCallback(
    async (indicationIds: string[]) => {
      try {
        updateFormData({ indicationIds })

        await rolodexPatch([
          {
            id: metadataEntryId,
            data: {
              section_info: { indicationIds },
            },
          },
        ])

        message.success('Indication selection saved successfully!')
      } catch {
        message.error('Failed to save indication selection')
      }
    },
    [updateFormData, metadataEntryId, rolodexPatch]
  )

  const handleSignoffFinish = useCallback(
    async (signatureData: SignatureData): Promise<SignoffState> => {
      try {
        const updatedSignoff: SignoffState = {
          ...(formData.signoff || { lastRevision: 0 }),
          [signatureData.role === 'supervisor'
            ? 'supervisorSignoff'
            : 'reviewerSignoff']: signatureData,
        }

        // Increment revision if reviewer is signing
        if (
          signatureData.role === 'reviewer' &&
          updatedSignoff.supervisorSignoff
        ) {
          updatedSignoff.lastRevision = (updatedSignoff.lastRevision || 0) + 1
        }

        // Update Rolodex first
        await rolodexPatch([
          {
            id: metadataEntryId,
            data: {
              signoff: updatedSignoff,
            },
          },
        ])

        // Then update local state
        updateFormData({
          signoff: updatedSignoff,
        })

        return updatedSignoff
      } catch (error) {
        console.error('Failed to save signoff:', error)
        throw error
      }
    },
    [formData.signoff, metadataEntryId, rolodexPatch, updateFormData]
  )

  return {
    formData,
    updateFormData,
    handleMainInfoFinish,
    handleCoverSectionFinish,
    handleAutocadDrawingFinish,
    handleAssessmentResultsFinish,
    handleIndicationIdsFinish,
    handleSignoffFinish,
  }
}
